/**
 * Helpers for downloading the current mesh artifact in different formats.
 *
 * GLB is the raw Tripo output and is fetched directly from a Firebase
 * Storage download URL. STL and OBJ are derived client-side from the
 * loaded glTF scene by walking its mesh nodes and feeding them to the
 * three.js exporters in `three/addons/exporters/`. This avoids spending
 * extra Tripo credits or adding a server-side conversion step.
 */

import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { STLExporter } from 'three/addons/exporters/STLExporter.js';
import { OBJExporter } from 'three/addons/exporters/OBJExporter.js';

/** Trigger a browser download for a Blob with the given filename. */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Direct download of the GLB blob behind a Firebase Storage download URL.
 *
 * We fetch the bytes ourselves (rather than setting an anchor `href`
 * directly to the URL) so the `download` attribute is honoured even
 * cross-origin via `firebasestorage.googleapis.com`.
 */
export async function downloadGlb(
  url: string,
  filename: string,
): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch GLB (${res.status} ${res.statusText})`);
  }
  const blob = await res.blob();
  triggerBlobDownload(blob, filename);
}

/**
 * Load the GLB at `url` and download it as a binary STL.
 *
 * `STLExporter` walks the scene graph itself and ignores non-mesh
 * objects, so we just hand it the root Group from the loaded glTF.
 */
export async function downloadStl(
  url: string,
  filename: string,
): Promise<void> {
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(url);
  const exporter = new STLExporter();
  const data = exporter.parse(gltf.scene, { binary: true });
  // `binary: true` returns a DataView; wrap its underlying buffer.
  const blob = new Blob(
    [data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)],
    { type: 'model/stl' },
  );
  triggerBlobDownload(blob, filename);
}

/**
 * Load the GLB at `url` and download it as an OBJ. `OBJExporter` also
 * walks the scene itself, so we pass the root Group.
 */
export async function downloadObj(
  url: string,
  filename: string,
): Promise<void> {
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(url);
  const exporter = new OBJExporter();
  const text = exporter.parse(gltf.scene);
  const blob = new Blob([text], { type: 'text/plain' });
  triggerBlobDownload(blob, filename);
}
